/**
 * アプリ全体のルーティング定義（Framework7-React）
 *
 * 方針/メモ:
 * - パス命名はスラッシュで階層化（例: /settings/app/, /review/daily/）。
 * - 設定は「/settings/」配下で、アプリ設定とアカウント設定を明確に分離。
 * - 軽量遷移のため、一部の画面でアニメーション無効化（options.animate = false）。
 * - ポップアップは popup: { component: ... } 形式でセクション末尾に配置。
 * - 最後に 404 フォールバックを置くこと。
 */
import { App } from "framework7-react";
import { ComponentProps } from "react";

import NotFoundPage from "@/pages/404";
import { AccountSettingPage } from "@/pages/AccountSettingPage";
import { ActivityLauncherSheetPage } from "@/pages/ActivityLauncherSheetPage";
import { ActivitySearchPopupPage } from "@/pages/ActivitySearchPopupPage";
import { AIReviewPage } from "@/pages/AIReviewPage";
import { AppAboutPage } from "@/pages/AppAboutPage";
import { AppSettingPage } from "@/pages/AppSettingPage";
import { AuthSignInPage } from "@/pages/AuthSignInPage";
import { AuthSignUpPage } from "@/pages/AuthSignUpPage";
import { CategoryEditPage } from "@/pages/CategoryEditPage";
import { CategoryListPage } from "@/pages/CategoryListPage";
import { ChangeCategorySheetPage } from "@/pages/ChangeCategorySheetPage";
import { ChangeEmailPage } from "@/pages/ChangeEmailPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { DailyDiaryListPage } from "@/pages/DailyDiaryListPage";
import { DailyDiaryPopupPage } from "@/pages/DailyDiaryPopupPage";
import { DailyReviewPage } from "@/pages/DailyReviewPage";
import { DebugSentryPage } from "@/pages/DebugSentryPage";
import { EntrySheetPage } from "@/pages/EntrySheetPage";
import { FeelingEditPage } from "@/pages/FeelingEditPage";
import { FeelingListPage } from "@/pages/FeelingListPage";
import { PasswordResetPage } from "@/pages/PasswordResetPage";
import { SettingListPage } from "@/pages/SettingListPage";
import { TopPage } from "@/pages/TopPage";
import { UpdateFeelingSheetPage } from "@/pages/UpdateFeelingSheetPage";
import { WeeklyReviewPage } from "@/pages/WeeklyReviewPage";

const routes: ComponentProps<typeof App>["routes"] = [
  /**
   * ================================
   * ホーム/トップ
   * ================================
   */
  {
    component: TopPage,
    path: "/",
  },

  /**
   * ================================
   * 認証
   * ================================
   */
  {
    component: AuthSignInPage,
    path: "/sign-in/",
  },
  {
    component: AuthSignUpPage,
    path: "/sign-up/",
  },
  {
    component: PasswordResetPage,
    path: "/password-reset/",
  },

  /**
   * ================================
   * レビュー
   * ================================
   * - /review/ に来た場合はデイリーへリダイレクト
   * - アニメーションを無効化
   */
  {
    path: "/review/",
    redirect: "/review/daily/",
  },
  {
    component: DailyReviewPage,
    options: {
      animate: false,
    },
    path: "/review/daily/",
  },
  {
    component: WeeklyReviewPage,
    options: {
      animate: false,
    },
    path: "/review/weekly/",
  },
  {
    component: AIReviewPage,
    options: {
      animate: false,
    },
    path: "/review/ai/",
  },

  /**
   * ================================
   * 日記
   * ================================
   */
  {
    component: DailyDiaryListPage,
    path: "/diary/",
  },
  {
    path: "/diary/daily/:date/",
    popup: {
      component: DailyDiaryPopupPage,
    },
  },

  /**
   * ================================
   * 設定
   * ================================
   */
  {
    component: SettingListPage,
    path: "/settings/",
  },

  /**
   * ================================
   * 設定 > アプリ設定
   * ================================
   */
  {
    component: AppSettingPage,
    options: {
      // アプリ設定配下はパララックスで遷移
      transition: "f7-parallax",
    },
    path: "/settings/app/",
  },
  {
    component: CategoryListPage,
    options: {
      transition: "f7-parallax",
    },
    path: "/settings/app/categories/",
  },
  {
    path: "/settings/app/categories/:categoryKey/",
    popup: {
      component: CategoryEditPage,
    },
  },
  {
    component: FeelingListPage,
    options: {
      transition: "f7-parallax",
    },
    path: "/settings/app/feelings/",
  },
  {
    path: "/settings/app/feelings/:feelingKey/",
    popup: {
      component: FeelingEditPage,
    },
  },

  /**
   * ================================
   * 設定 > アカウント設定
   * ================================
   */
  {
    component: AccountSettingPage,
    options: {
      transition: "f7-parallax",
    },
    path: "/settings/account/",
  },
  {
    component: ChangeEmailPage,
    options: {
      transition: "f7-parallax",
    },
    path: "/settings/account/email",
  },
  {
    component: ChangePasswordPage,
    options: {
      transition: "f7-parallax",
    },
    path: "/settings/account/password",
  },

  /**
   * ================================
   * 設定 > アプリ情報
   * ================================
   */
  {
    component: AppAboutPage,
    options: {
      transition: "f7-parallax",
    },
    path: "/settings/app-about/",
  },

  /**
   * ================================
   * ポップアップ
   * ================================
   */
  {
    path: "/modals/activity-search/",
    popup: {
      component: ActivitySearchPopupPage,
    },
  },
  {
    path: "/modals/entries/:entryId",
    popup: {
      component: EntrySheetPage,
    },
  },
  {
    path: "/modals/activity-launcher",
    popup: {
      component: ActivityLauncherSheetPage,
    },
  },
  {
    path: "/modals/entries/:entryId/update-feeling",
    popup: {
      component: UpdateFeelingSheetPage,
    },
  },
  {
    path: "/modals/entries/:entryId/change-category",
    popup: {
      component: ChangeCategorySheetPage,
    },
  },

  /**
   * ================================
   * Debug
   * ================================
   */
  {
    component: DebugSentryPage,
    path: "/debug/sentry/",
  },

  /**
   * ================================
   * フォールバック（404）
   * ================================
   */
  {
    component: NotFoundPage,
    path: "(.*)",
  },
];

export default routes;
